import { identity } from 'lodash'

import {
  ADD_BEAT,
  EDIT_BEAT_TITLE,
  REORDER_BEATS,
  DELETE_BEAT,
  AUTO_SORT_BEAT,
  INSERT_BEAT,
  EXPAND_BEAT,
  COLLAPSE_BEAT,
  LOAD_BEATS,
  APPEND_TOP_LEVEL_BEAT,
  RESTRUCTURE_TIMELINE,
} from '../constants/ActionTypes'
import { beat } from '../store/initialState'
import selectors from '../selectors'

const { timelineViewIsStackedSelector } = selectors(identity)

/**
 * @typedef {import('../index').Dispatch} Dispatch
 * @typedef {import('../index').GetState} GetState
 * @typedef {import('../index').Beat} Beat
 * @typedef {import('../index').PlottrFileBeatsEntry} PlottrFileBeatsEntry
 * @typedef {import('../index').PlottrFileBeats} PlottrFileBeats
 * @typedef {import('../index').HierarchyLevel} HierarchyLevel
 */

/**
 * @typedef {{ type: 'AUTO_SORT_BEAT', id: number, bookId: number | 'series', isSeries: boolean }} AutoSortBeatAction
 * @typedef {{ type: 'DELETE_BEAT', id: number, bookId: number | 'series', actTab: number }} DeleteBeatAction
 * @typedef {{ type: 'INSERT_BEAT', bookId: 'series' | number, peerBeatId: number, isStacked: boolean, parentId: null | number, timelineViewIsStacked: boolean }} InsertBeatAction
 * @typedef {{ type: 'UNSAFE_SET_BEATS', bookId: 'series' | number, newBookId: 'series' | number, beats: PlottrFileBeats }} UnsafeSetBeatsAction
 */

/**
 * @typedef {ReturnType<typeof addBeat> | ReturnType<typeof editBeatTitle> | ReturnType<typeof reorderBeats> | ReturnType<typeof expandBeat> | ReturnType<typeof collapseBeat> | ReturnType<typeof appendTopLevelBeat> | ReturnType<typeof restructureTimeline> | ReturnType<typeof load> | AutoSortBeatAction | DeleteBeatAction | InsertBeatAction | UnsafeSetBeatsAction} BeatActions
 */

/**
 * @param {number | 'series'} bookId
 * @param {number | null} parentId
 * @return {{ type: 'ADD_BEAT', title: String, bookId: 'series' | number, parentId: (number | Null) }}
 */
export function addBeat(bookId, parentId) {
  return { type: ADD_BEAT, title: beat.title, bookId, parentId }
}

/**
 * @param {number} id
 * @param {number | 'series'} bookId
 * @param {String} title
 * @return {{ type: 'EDIT_BEAT_TITLE', id: number, bookId: number | 'series', title: String }}
 */
export function editBeatTitle(id, bookId, title) {
  return { type: EDIT_BEAT_TITLE, id, bookId, title }
}

/**
 * @param {number} beatId
 * @param {number} beatDroppedOnto
 * @param {number | 'series'} bookId
 * @return {{ type: 'REORDER_BEATS', beatId: number, beatDroppedOnto: number, bookId: number | 'series' }}
 */
export function reorderBeats(beatId, beatDroppedOnto, bookId) {
  return { type: REORDER_BEATS, beatId, beatDroppedOnto, bookId }
}

/**
 * @param {number | 'series'} bookId
 * @param {number} peerBeatId
 * @param {boolean} timelineViewIsStacked
 * @return {{ type: INSERT_BEAT, bookId: 'series' | number, peerBeatId: number, isStacked: boolean }}
 */
export const _insertBeat = (bookId, peerBeatId, timelineViewIsStacked) => {
  return { type: INSERT_BEAT, bookId, peerBeatId, isStacked: timelineViewIsStacked }
}

/**
 * @param {number | 'series'} bookId
 * @param {number} peerBeatId
 * @return {function(Dispatch, GetState): Void}
 */
export const insertBeat = (bookId, peerBeatId) => (dispatch, getState) => {
  const state = getState()
  const timelineViewIsStacked = timelineViewIsStackedSelector(state)

  dispatch(
    // NOTE(ed): we add more attributes in the root reducer
    //
    // @ts-ignore
    _insertBeat(bookId, peerBeatId, timelineViewIsStacked)
  )
}

/**
 * @param {number} id
 * @param {number | 'series'} bookId
 * @return {{ type: 'DELETE_BEAT', id: number, bookId: number | 'series' }}
 */
export function deleteBeat(id, bookId) {
  return { type: DELETE_BEAT, id, bookId }
}

/**
 * @param {number} id
 * @param {number | 'series'} bookId
 * @return {{ type: 'AUTO_SORT_BEAT', id: number, bookId: number | 'series' }}
 */
export function autoSortBeat(id, bookId) {
  return { type: AUTO_SORT_BEAT, id, bookId }
}

/**
 * @param {number} id
 * @param {number | 'series'} bookId
 * @return {{ type: 'EXPAND_BEAT', id: number, bookId: number | 'series' }}
 */
export function expandBeat(id, bookId) {
  return { type: EXPAND_BEAT, id, bookId }
}

/**
 * @param {number} id
 * @param {number | 'series'} bookId
 * @return {{ type: 'COLLAPSE_BEAT', id: number, bookId: number | 'series' }}
 */
export function collapseBeat(id, bookId) {
  return { type: COLLAPSE_BEAT, id, bookId }
}

/**
 * @param {number | 'series'} bookId
 * @return {{ type: 'APPEND_TOP_LEVEL_BEAT', bookId: number | 'series' }}
 */
export function appendTopLevelBeat(bookId) {
  return { type: APPEND_TOP_LEVEL_BEAT, bookId }
}

/**
 * @param {Array<Beat>} flatBeats
 * @param {Array<number>} beatHierarchyLevels
 * @return {{ type: 'RESTRUCTURE_TIMELINE', flatBeats: Array<Beat>, beatHierarchyLevels: Array<number> }}
 */
export function restructureTimeline(flatBeats, beatHierarchyLevels) {
  return { type: RESTRUCTURE_TIMELINE, flatBeats, beatHierarchyLevels }
}

/**
 * @param {boolean} patching
 * @param {PlottrFileBeatsEntry} beats
 * @return {{ type: 'LOAD_BEATS', patching: boolean, beats: PlottrFileBeatsEntry }}
 */
export function load(patching, beats) {
  return { type: LOAD_BEATS, patching, beats }
}
